/**
 * Pipedrive Products Page
 * List of products with search, filtering, and pagination
 */

import { useState } from 'react'
import { usePipedriveProducts, usePipedriveProductFilters } from '@/hooks/use-pipedrive-api'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Package, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import type { PipedriveProduct } from '@/types/pipedrive'

export default function PipedriveProducts() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [active, setActive] = useState('')
  const limit = 25

  const { data, isLoading, error } = usePipedriveProducts({
    page,
    limit,
    search,
    category: category || undefined,
    active: active || undefined,
  })

  const { data: filters } = usePipedriveProductFilters()

  const products = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = data?.pages ?? 1

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const clearFilters = () => {
    setSearch('')
    setCategory('')
    setActive('')
    setPage(1)
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Products</h1>
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          Failed to load products. Please try again later.
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="h-6 w-6" aria-hidden="true" />
            Products
          </h1>
          <p className="text-muted-foreground">{total.toLocaleString()} products</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9"
                  aria-label="Search products"
                />
              </div>
            </div>
            <Select
              value={category || '__all__'}
              onValueChange={(v) => {
                setCategory(v === '__all__' ? '' : v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[180px]" aria-label="Filter by category">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Categories</SelectItem>
                {filters?.categories?.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={active || '__all__'}
              onValueChange={(v) => {
                setActive(v === '__all__' ? '' : v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[150px]" aria-label="Filter by status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {filters?.activeOptions?.map((a) => (
                  <SelectItem key={a} value={a}>
                    {a === 'Yes' ? 'Active' : 'Inactive'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Price (CAD)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-40" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-28" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-6 w-16" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20 ml-auto" />
                    </TableCell>
                  </TableRow>
                ))
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No products found
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product: PipedriveProduct) => (
                  <TableRow key={product.id}>
                    <TableCell className="font-medium">{product.name || '-'}</TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-1 py-0.5 rounded">
                        {product.product_code || '-'}
                      </code>
                    </TableCell>
                    <TableCell>{product.category || '-'}</TableCell>
                    <TableCell>{product.owner || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={product.active === 'Yes' ? 'default' : 'secondary'}>
                        {product.active === 'Yes' ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {product.price_cad ? `$${product.price_cad.toLocaleString()}` : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of{' '}
            {total.toLocaleString()}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
